import React, { Suspense } from 'react';
import { Helmet } from 'react-helmet';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useMutation } from 'react-query';
import { useRecoilState } from 'recoil';
import { useParams } from 'react-router-dom';
import { useAxios } from 'hooks';
import { endPoints } from 'api/endpoints';
import { catalogs } from 'store';
import { orderId, sspSubmitOrder, toggleSspModels } from 'shared/store';
import ProvisioningStyles from 'pages/provisioning/vm/style';
import getProvisioningTitle from 'utils/getProvisioningTitle';
import logger from 'utils/logger';
import { formOrderObjFromCart, formOrderObjFromOrderDetails, resetOrder, resetToggleSspModal } from './utils';

const ProvisioningHeader = React.lazy(() => import('pages/provisioning/azure/edap-node-pool/ProvisioningHeader'));
const SspForm = React.lazy(() => import('pages/provisioning/azure/edap-node-pool/SspForm'));
const AddedToCartConfirmModal = React.lazy(() => import('pages/provisioning/azure/edap-node-pool/AddedToCartConfirmModal'));
const UpdatedToCartConfirmModal = React.lazy(() => import('pages/provisioning/azure/edap-node-pool/UpdatedToCartConfirmModal'));
const ShowCostDetails = React.lazy(() => import('pages/provisioning/azure/edap-node-pool/ShowCostDetails'));

const EdapNodePoolProvisioningPage = () => {
    const [isLoading, setLoading] = React.useState(true);
    const [cmpCatalogs] = useRecoilState(catalogs);
    const [order, setOrder] = useRecoilState(sspSubmitOrder);
    const [, resetOrderId] = useRecoilState(orderId);
    const [, resetToggleSspModels] = useRecoilState(toggleSspModels);
    const { catalogCode, cartItemId, operation, catalogServiceId } = useParams();
    const axiosInstance = useAxios();

    const vmTypeApi = useMutation((payload) => axiosInstance.post(endPoints.ssp.vm.vmTypes, payload));

    const getContactDetails = async (gpid) => {
        if (!gpid) return null;
        try {
            const response = await axiosInstance.get(`${endPoints.riServices.RI003.appOwner}?key=${gpid}`);
            return response?.data?.[0] || null;
        } catch (error) {
            logger.error('Error fetching contact details:', error);
            return null;
        }
    };

    const buildContactObject = (contactData) => {
        if (!contactData) return null;
        const label = contactData?.gpid && contactData?.name ? `${contactData.gpid} - ${contactData.name}` : contactData?.gpid || contactData?.name || null;
        return {
            label,
            gpid: contactData?.gpid || null,
            name: contactData?.name || null,
            email: contactData?.email || null,
        };
    };

    const getMatchedVmTypeDetails = async (orderObj) => {
        const { region, environment, cloudService } = orderObj || {};
        const clusterData = orderObj?.clusterDetails?.data || orderObj?.clusterDetails || {};
        const subscriptionId = clusterData?.subscriptionId || clusterData?.subscriptionID || null;
        const vmTypeName = orderObj?.vmType;

        if (!(vmTypeName && region && subscriptionId && environment && cloudService)) {
            return {
                vmType: vmTypeName || null,
                vmTypeDetails: null,
            };
        }

        try {
            const response = await vmTypeApi.mutateAsync({
                region,
                subscriptionID: subscriptionId,
                environment,
                hyperVGeneration: 'V1',
                catalogService: cloudService,
            });

            const vmTypes = response?.data || [];
            const matchedVmType = vmTypes.find((vm) => vm?.name === vmTypeName);

            return {
                vmType: matchedVmType?.displayName || null,
                vmTypeDetails: matchedVmType || null,
            };
        } catch (error) {
            logger.error('Error fetching vmType details:', error);
            return {
                vmType: vmTypeName || null,
                vmTypeDetails: null,
            };
        }
    };

    const getMatchedClusterDetails = async (orderObj) => {
        const region = orderObj?.region;
        const clusterName = orderObj?.cluster;

        if (!(region && clusterName)) {
            return orderObj?.clusterDetails || null;
        }

        try {
            const response = await axiosInstance.get(endPoints.ssp.aks.clusterDetails(region));
            const clusters = response?.data || [];
            const matchedCluster = clusters.find((item) => item?.name === clusterName) || null;

            if (!matchedCluster) {
                return orderObj?.clusterDetails || null;
            }

            return {
                label: matchedCluster?.name,
                value: matchedCluster?.name,
                data: matchedCluster,
            };
        } catch (error) {
            return orderObj?.clusterDetails || null;
        }
    };

    async function getCosts() {
        const { data } = await axiosInstance.get(`${endPoints.catalog.get}/${catalogCode}/costs`);
        return data;
    }

    async function checkCatalog() {
        const data = cmpCatalogs?.find((item) => item?.catalogDefinitionId === catalogCode);
        const costs = await getCosts();
        if (cartItemId && operation && !catalogServiceId) {
            const { data: cartItem } = await axiosInstance.get(`${endPoints.cart.getCartItem}/${cartItemId}`);
            const orderObj = await formOrderObjFromCart(cartItem);

            // Fetch contact details from API
            const supportContactData = await getContactDetails(orderObj?.supportContactDetails?.gpid);
            const businessContactData = await getContactDetails(orderObj?.businessContactDetails?.gpid);

            const clusterDetails = await getMatchedClusterDetails(orderObj);

            // Fetch and match vmType details
            const { vmType, vmTypeDetails } = await getMatchedVmTypeDetails({ ...orderObj, clusterDetails });

            setOrder((previous) => ({
                ...previous,
                ...orderObj,
                supportContact: buildContactObject(supportContactData)?.label || null,
                supportContactDetails: buildContactObject(supportContactData),
                businessContact: buildContactObject(businessContactData)?.label || null,
                businessContactDetails: buildContactObject(businessContactData),
                cluster: clusterDetails?.value || clusterDetails?.data?.name || orderObj?.cluster || null,
                clusterDetails,
                vmType,
                vmTypeDetails,
                availabilityZoneOptions: vmTypeDetails?.availabilityZones || previous?.availabilityZoneOptions || null,
                emptyCost: costs,
            }));
        } else if (cartItemId && operation && catalogServiceId) {
            const { data: orderDetails } = await axiosInstance.get(`${endPoints.order.orderDetails}/${cartItemId}`);
            const orderObj = await formOrderObjFromOrderDetails(catalogServiceId, orderDetails);

            // Fetch contact details from API
            const supportContactData = await getContactDetails(orderObj?.supportContactDetails?.gpid);
            const businessContactData = await getContactDetails(orderObj?.businessContactDetails?.gpid);

            const clusterDetails = await getMatchedClusterDetails(orderObj);

            // Fetch and match vmType details
            const { vmType, vmTypeDetails } = await getMatchedVmTypeDetails({ ...orderObj, clusterDetails });

            setOrder((previous) => ({
                ...previous,
                ...orderObj,
                supportContact: buildContactObject(supportContactData)?.label || null,
                supportContactDetails: buildContactObject(supportContactData),
                businessContact: buildContactObject(businessContactData)?.label || null,
                businessContactDetails: buildContactObject(businessContactData),
                cluster: clusterDetails?.value || clusterDetails?.data?.name || orderObj?.cluster || null,
                clusterDetails,
                vmType,
                vmTypeDetails,
                availabilityZoneOptions: vmTypeDetails?.availabilityZones || previous?.availabilityZoneOptions || null,
                emptyCost: costs,
            }));
        } else {
            setOrder((previous) => ({
                ...previous,
                catalogCode,
                catalogType: data?.displayName,
                cloudProvider: data?.cloudProvider,
                cloudService: data?.cloudService,
                validationStatus: 'clearValidation',
                isTouched: false,
                emptyCost: costs,
            }));
        }
        setLoading(false);
    }

    const reset = () => {
        setOrder({
            ...order,
            ...resetOrder,
        });
        resetToggleSspModels(resetToggleSspModal);
        resetOrderId('');
    };

    React.useEffect(() => {
        if (cmpCatalogs?.length && catalogCode) {
            setLoading(true);
            reset();
            checkCatalog();
        }
    }, [cmpCatalogs, catalogCode]);

    /* eslint-disable arrow-body-style */
    React.useEffect(() => {
        return () => reset();
        // eslint-disable-next-line
    }, []);

    return (
        <>
            <Helmet>
                <title>{getProvisioningTitle(cmpCatalogs, catalogCode)}</title>
            </Helmet>
            <Suspense
                fallback={
                    <Box sx={ProvisioningStyles.provisioning_wrapper}>
                        <CircularProgress color="inherit" size={16} sx={ProvisioningStyles.circularProgressColor} />
                        <Typography variant="body2" sx={ProvisioningStyles.circularProgressColor}>
                            Loading
                        </Typography>
                    </Box>
                }
            >
                {isLoading && (
                    <Box sx={ProvisioningStyles.provisioning_wrapper}>
                        <CircularProgress color="inherit" size={16} sx={ProvisioningStyles.circularProgressColor} />
                        <Typography variant="body2" sx={ProvisioningStyles.circularProgressColor}>
                            Loading
                        </Typography>
                    </Box>
                )}
                {!isLoading && (
                    <Box sx={ProvisioningStyles.provisioningBox} data-testid="aws/dynamo-db-wrapper">
                        <ProvisioningHeader />
                        <Box sx={ProvisioningStyles.sspform_wrapper} className="contentContainer">
                            <SspForm />
                        </Box>
                        <br />
                        <AddedToCartConfirmModal />
                        <UpdatedToCartConfirmModal />
                        <ShowCostDetails />
                    </Box>
                )}
            </Suspense>
        </>
    );
};

export default EdapNodePoolProvisioningPage;
